// How long a PR revision has been waiting for its preview deployment URL, and whether that wait has
// gone on long enough to read as a failed build rather than as progress.
//
// The threshold is the backend's PREVIEW_STALL_AFTER_MS (netlify/functions/lib/deployment-status.ts)
// rather than a second copy of it: the GitHub tab, the Test Run page, and what the chat agent says
// about a PR must not disagree about whether a preview is late or missing.
import { PREVIEW_STALL_AFTER_MS } from '../../netlify/functions/lib/deployment-status'

/** The fields of a PullRequest this module reads — narrowed so callers can pass any PR-ish row. */
export interface PreviewWaitFacts {
  deployed_url: string | null
  awaiting_deployment: boolean
  awaiting_deployment_since: string
}

export interface PreviewWait {
  /** Minutes since QA started waiting, or null when the timestamp is unusable. */
  minutes: number | null
  /** Past the point where a preview normally would have appeared. */
  stalled: boolean
}

/** Null when this revision is not waiting on a deployment (one was found, or QA never held for one). */
export function previewWait(pr: PreviewWaitFacts, nowMs: number = Date.now()): PreviewWait | null {
  if (pr.deployed_url || !pr.awaiting_deployment) return null
  const started = new Date(pr.awaiting_deployment_since).getTime()
  if (!Number.isFinite(started)) return { minutes: null, stalled: false }
  const waitedMs = Math.max(0, nowMs - started)
  return { minutes: Math.round(waitedMs / 60_000), stalled: waitedMs >= PREVIEW_STALL_AFTER_MS }
}

/** Use the persisted timeout explanation, including for revisions with no test session. */
export function previewDeploymentFailure(notices: readonly { kind: string; source?: string; content: string }[]): string | null {
  return notices.find(notice => notice.kind === 'finished' && notice.source?.startsWith('preview-never-arrived:'))?.content.trim() || null
}
